
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const units = [
  { id: 'mm2', name: 'Square Millimeters (mm²)' },
  { id: 'cm2', name: 'Square Centimeters (cm²)' },
  { id: 'dm2', name: 'Square Decimeters (dm²)' },
  { id: 'm2', name: 'Square Meters (m²)' },
  { id: 'a', name: 'Ares (a)' },
  { id: 'ha', name: 'Hectares (ha)' },
  { id: 'km2', name: 'Square Kilometers (km²)' },
  { id: 'in2', name: 'Square Inches (in²)' },
  { id: 'ft2', name: 'Square Feet (ft²)' },
  { id: 'yd2', name: 'Square Yards (yd²)' },
  { id: 'ac', name: 'Acres (ac)' },
  { id: 'mi2', name: 'Square Miles (mi²)' },
];

// Conversion rates to square meters
const conversionRates: Record<string, number> = {
  'mm2': 0.000001,
  'cm2': 0.0001,
  'dm2': 0.01,
  'm2': 1,
  'a': 100,
  'ha': 10000,
  'km2': 1000000,
  'in2': 0.00064516,
  'ft2': 0.09290304,
  'yd2': 0.83612736,
  'ac': 4046.8564224,
  'mi2': 2589988.110336,
};

const AreaConverter = () => {
  const [fromValue, setFromValue] = useState<string>('1');
  const [fromUnit, setFromUnit] = useState<string>('m2');
  const [toUnit, setToUnit] = useState<string>('ft2');
  const [toValue, setToValue] = useState<string>('');

  useEffect(() => {
    convert();
  }, [fromValue, fromUnit, toUnit]);

  const convert = () => {
    try {
      const inputValue = parseFloat(fromValue);
      if (!isNaN(inputValue)) {
        // Convert to base unit (square meters)
        const baseValue = inputValue * conversionRates[fromUnit];
        // Convert from base unit to target unit
        const result = baseValue / conversionRates[toUnit];
        setToValue(result.toFixed(10).replace(/\.?0+$/, ""));
      } else {
        setToValue('');
      }
    } catch (error) {
      console.error('Conversion error:', error);
      setToValue('Error');
    }
  };

  const handleFromValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFromValue(e.target.value);
  };

  const handleFromUnitChange = (value: string) => {
    setFromUnit(value);
  };

  const handleToUnitChange = (value: string) => {
    setToUnit(value);
  };

  const swapUnits = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
    setFromValue(toValue);
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>Area Converter</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">From:</label>
            <div className="grid grid-cols-3 gap-2">
              <Input 
                type="number" 
                value={fromValue} 
                onChange={handleFromValueChange} 
                className="col-span-2"
              />
              <Select value={fromUnit} onValueChange={handleFromUnitChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Unit" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex justify-center">
            <button 
              onClick={swapUnits}
              className="p-2 rounded-full hover:bg-muted"
            >
              ⇅
            </button>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">To:</label>
            <div className="grid grid-cols-3 gap-2">
              <Input 
                type="text" 
                value={toValue} 
                readOnly 
                className="col-span-2"
              />
              <Select value={toUnit} onValueChange={handleToUnitChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Unit" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AreaConverter;
